import {
  ArticleBlockJson,
  BlockBackgroundsType,
  ChartSpec,
  ColumnContainerJson,
  BlockElementJson,
  Present,
  PresentOption,
  Section,
  TableCellJson,
  AppShellProps,
  ChartLabel,
  BlockProjectData,
} from '/types';

// In ES6 `%` is actually 'remainder' and allows negative outputs
export const mod = (n: number, m: number): number => ((n % m) + m) % m;

export const isViewedBlock = (
  block: Element,
  completely: boolean = false,
): boolean => {
  const rect = block && block.getBoundingClientRect();
  if (rect) {
    const isInViewport = completely
      ? rect.y >= 0 && rect.y + rect.height < window.innerHeight
      : rect.y >= 0 && rect.y < window.innerHeight;
    const coversWholeViewport =
      rect.y <= 0 && rect.y + rect.height >= window.innerHeight;

    return isInViewport || coversWholeViewport;
  }

  return false;
};

// Copied from https://stackoverflow.com/a/8028584/1224456
export const calculateScrollPercentage = (): number => {
  const h = document.documentElement,
    b = document.body,
    st = 'scrollTop',
    sh = 'scrollHeight';

  return ((h[st] || b[st]) / ((h[sh] || b[sh]) - h.clientHeight)) * 100;
};

export const getSections = (blocks: ArticleBlockJson[]): Section[] => {
  const sections: Section[] = [];
  let previousSection: Section | null = null;
  blocks.forEach((block, index) => {
    const title = JSON.parse(block.json)?.title?.content || '';

    const section: Section = {
      end: 0,
      id: block.id,
      index: index,
      title: title,
    };
    if (
      ['block-section-header', 'block-sub-section-header'].includes(block.type)
    ) {
      if (block.type === 'block-section-header') {
        section.subsections = [];
        sections.push(section);
      } else {
        const parent = sections[sections.length - 1];
        if (parent) {
          parent.subsections = parent.subsections || [];
          parent.subsections.push(section);
        }
      }
      if (previousSection) {
        previousSection.end = index;
      }
      previousSection = section;
    }
  });

  if (previousSection) {
    (previousSection as Section).end = blocks.length - 1;
  }
  return sections;
};

/* Used for centering non-content columns, this function removes the content
 * column if its every element should be hidden in keynote.
 * Only list elements and selected texts can show in keynote.
 */
export const filterKeynoteColumns = (
  columns: ColumnContainerJson[],
  presentMode?: Present,
  presentOption?: PresentOption,
): ColumnContainerJson[] =>
  presentMode === Present.Keynote && presentOption !== PresentOption.Full
    ? columns.filter((column: ColumnContainerJson) => {
        if (column.name === 'content' && column.elements?.content?.content) {
          return column.elements.content.content.match(
            /<ol>|<ul>|class="selected-for-presentation"/,
          );
        }
        return true;
      })
    : columns;

/* Filter column content down for keynote presentations.
 * Default Keynote   - Keep only lists
 * Selected Keynote  - keep lists and selected text
 * Full-text keynote - keep all content
 * Full-written      - keep all content
 */
export const keynoteContentFilter = (
  content: string = '',
  presentMode?: Present,
  presentOption?: PresentOption,
): string => {
  if (presentMode !== Present.Keynote || presentOption === PresentOption.Full) {
    return content;
  }

  const dom = new DOMParser().parseFromString(content, 'text/html');
  const selectClass = 'selected-for-presentation';

  return Array.from(
    dom.body.childNodes as NodeListOf<HTMLElement>,
    (rootNode: HTMLElement) => {
      if (presentOption === PresentOption.Selected) {
        if (
          rootNode.nodeName === 'SPAN' &&
          rootNode.classList.contains(selectClass)
        ) {
          return rootNode.innerHTML;
        }

        if (rootNode.nodeName === 'P') {
          const selectedText = Array.from(
            rootNode.querySelectorAll(`span.${selectClass}`),
            (span) => span.innerHTML,
          ).join(' ');
          return `<p>${selectedText}</p>`;
        }
      } else if (!['OL', 'UL'].includes(rootNode.nodeName)) {
        return '';
      }

      return rootNode.outerHTML;
    },
  ).join(' ');
};

/* Get string from content with `keynoteContentFilter`. CMS `hidden` aware. */
export const contentFilter = (
  content: BlockElementJson | undefined,
  presentMode: Present,
  presentOption: PresentOption | undefined,
): string => {
  if (content === undefined || content.hidden) {
    return '';
  }
  return keynoteContentFilter(content.content, presentMode, presentOption);
};

export const isDarkBackground = (background: BlockBackgroundsType): boolean => {
  return !['bg-white', 'bg-grey'].includes(background);
};

/* Content types that come from the server need to be modified to be used
 * as part of a URL path. For example: "Industry Trend" -> "trend"
 */
export const contentTypeToPath = (contentType: string): string => {
  if (contentType == 'Innovative Product') return 'innovative-product';
  if (contentType == 'Internal Only') return 'internal-only';
  if (contentType == 'Industry Trend') return 'trend';
  if (contentType == 'Consumer Trend') return 'trend';
  return contentType.toLowerCase();
};

// Matching a whitespace character, followed by a match of our special
// characters, followed by a negative lookahead for a whitespace
const CHART_SPECIAL_CHAR_REGEX = /(\s)?[/.,&+;\\]+(?!\s)/g;

const cleanLabel = (pattern: RegExp, label: string): string =>
  label.replace(pattern, (character, whitespace) =>
    // There's no negative lookbehind in Javascript!
    // So we match the space before the character we're interested in,
    // and then act depending on whether whitespace was matched
    whitespace ? character : `${character} `,
  );

/**
 * Add spaces after specified characters to labels on x and y axes of the
 * graph, to ensure labels are wrapped on multiple lines.
 * e.g "foo/bar+baz" => "foo/ bar+ baz"
 */
export const cleanLabels = (config: ChartSpec): void => {
  const axes: ChartLabel[][] = [];
  ['xAxis', 'yAxis'].forEach((axis) => {
    const specAxis = config.options[(axis as 'xAxis') ?? 'yAxis'];
    if (Array.isArray(specAxis)) {
      specAxis.forEach((axis) => {
        axes.push(axis?.values ?? []);
      });
    } else {
      axes.push(specAxis?.values ?? []);
    }
  });

  axes.forEach((values) => {
    values.forEach((value, index) => {
      if (typeof value === 'string') {
        values[index] = cleanLabel(CHART_SPECIAL_CHAR_REGEX, value);
      } else {
        values[index] = {
          ...value,
          label: cleanLabel(CHART_SPECIAL_CHAR_REGEX, value.label),
        };
      }
    });
  });
};

type Rows = TableCellJson[][];

export const splitTableHeadAndBody = (rows: Rows): [Rows, Rows] => {
  const firstBodyRow = rows.findIndex(
    (row) => !row.every((cell) => cell.header),
  );
  if (firstBodyRow < 0) {
    return [rows, []];
  }
  return [rows.slice(0, firstBodyRow), rows.slice(firstBodyRow)];
};

/**
 * Finding the index of the first row that is hidden by default, unless
 * the "show more" button is clicked. This is different than hiding a specific
 * number of rows as we want to take into account cells' rowspan values and
 * hide rows that have cells with rowspans that are beyond `maxRows`.
 */
export const findFirstExtraRowIndex = (rows: Rows, maxRows: number): number => {
  let rowspanCount = 0;
  let skipRows = 0;
  for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
    if (skipRows < 1) {
      const rowSpans = rows[rowIndex].map((cell) => cell.rowspan);
      const maxRowspan = Math.max(...rowSpans);
      rowspanCount += maxRowspan;
      if (maxRowspan > 1) {
        skipRows += maxRowspan - 1;
      }
    } else {
      skipRows -= 1;
    }
    if (rowspanCount > maxRows) {
      return rowIndex;
    }
  }
  return rows.length;
};

/**
 * Sets feature flags to desired state in storybook
 * Use in AppshellContext provider wrapping storybook component
 */
export const mockFeatureFlags = (featureFlagState: boolean): AppShellProps =>
  (({
    featureFlags: { getValue: () => Promise.resolve(featureFlagState) },
  } as unknown) as AppShellProps);

/**
 * Creates an object from block data in the shape required for the project service
 */
export const createBlockProjectData = (
  articleId: string,
  blockId: string,
  blockType: string,
): BlockProjectData => {
  const blockData = {
    item_uuid: `${articleId}#${blockId}`,
    title: '',
    version: '',
    product: 'effortless',
    slide_type: `${blockType}`,
  };
  return blockData;
};

/**
 * Gets the project data for a given section
 */
export const getSectionProjectData = (
  currentBlockId: string,
  blocksProjectData: BlockProjectData[],
): BlockProjectData[] => {
  const blocks = blocksProjectData;
  const sectionHeaderIndex = blocks.findIndex((block) =>
    block.item_uuid.includes(currentBlockId),
  );
  const sectionData = [blocks[sectionHeaderIndex]];
  for (let i = sectionHeaderIndex + 1; i < blocks.length; i++) {
    if (blocks[i].slide_type === 'block-section-header') {
      break;
    }
    sectionData.push(blocks[i]);
  }
  return sectionData;
};

/**
 * Removes any html tags from a string.
 */
export const stripTags = (html: string | undefined): string | undefined => {
  if (html === undefined) {
    return undefined;
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const walker = document.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);

  const texts = [];
  let node;
  while ((node = walker.nextNode())) {
    texts.push(node.nodeValue);
  }
  return texts.join('');
};
